/**
 * WatchHintChip — small editorial cross-reference to a single video
 * tile from the /resources#watch library. Surfaces in-context entry
 * points to the field-guide videos without duplicating their copy.
 *
 * Two variants:
 *   - compact (default): one-line chip with sage play stencil, tile
 *     title, and duration. For filter rails and dense sidebars.
 *   - card: bigger block with eyebrow, title, body, duration. For
 *     places where there's room for more context (e.g. a sidebar
 *     beneath a CTA).
 */
import { Link } from "react-router-dom";
import { findWatchTile } from "../lib/watchTracks";

export function WatchHintChip({ slug, variant = "compact", testid }) {
  const tile = findWatchTile(slug);
  if (!tile) return null;

  const href = `/resources#watch`;

  if (variant === "card") {
    return (
      <Link
        to={href}
        data-testid={testid || `watch-hint-card-${slug}`}
        style={{
          display: "block",
          textDecoration: "none",
          padding: 14,
          borderRadius: 4,
          border: "1px solid #DDD9CE",
          background: "#FBFAFD",
          transition: "background 160ms ease, border-color 160ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#F4F2EC";
          e.currentTarget.style.borderColor = "#B8BFA0";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#FBFAFD";
          e.currentTarget.style.borderColor = "#DDD9CE";
        }}
      >
        <div
          className="flex items-baseline"
          style={{ gap: 10, flexWrap: "wrap" }}
        >
          <span
            className="wn-label"
            style={{ color: "#3A4A35", letterSpacing: "0.08em" }}
          >
            Watch · {tile.trackEyebrow}
          </span>
          <span
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 10.5,
              color: "#6C6B85",
              letterSpacing: "0.04em",
            }}
          >
            {tile.duration}
          </span>
        </div>
        <div
          className="mt-1.5"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 16,
            color: "#1B1F4B",
            lineHeight: 1.3,
            letterSpacing: "-0.005em",
          }}
        >
          {tile.title}
        </div>
        {tile.body && (
          <div
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 12.5,
              lineHeight: 1.55,
            }}
          >
            {tile.body}
          </div>
        )}
      </Link>
    );
  }

  // Compact one-line chip
  return (
    <Link
      to={href}
      data-testid={testid || `watch-hint-chip-${slug}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        textDecoration: "none",
        padding: "8px 10px 8px 8px",
        borderRadius: 4,
        border: "1px solid #DDD9CE",
        background: "#FBFAFD",
        transition: "background 160ms ease, border-color 160ms ease",
        maxWidth: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#F4F2EC";
        e.currentTarget.style.borderColor = "#B8BFA0";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#FBFAFD";
        e.currentTarget.style.borderColor = "#DDD9CE";
      }}
    >
      {/* Sage play stencil */}
      <span
        aria-hidden
        style={{
          flex: "0 0 auto",
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#E8EAD9",
          border: "1px solid #B8BFA0",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="8" height="9" viewBox="0 0 8 9">
          <polygon points="0,0 0,9 8,4.5" fill="#3A4A35" />
        </svg>
      </span>
      <span style={{ minWidth: 0, lineHeight: 1.25 }}>
        <span
          className="wn-label"
          style={{
            display: "block",
            color: "#3A4A35",
            letterSpacing: "0.08em",
            fontSize: 9.5,
          }}
        >
          Watch · {tile.duration}
        </span>
        <span
          style={{
            display: "block",
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#1B1F4B",
            fontSize: 12.5,
            lineHeight: 1.3,
            marginTop: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {tile.title}
        </span>
      </span>
    </Link>
  );
}

export default WatchHintChip;
